import clsx from 'clsx';
import dayjs from 'dayjs';
import { useState, Activity, useEffect } from 'react';

import {
  IconEye,
  IconEdit,
  IconPlus,
  IconTrash,
  IconSearch,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Input from '@/components/Input/Input';
import CustomTable from '@/components/Table/index';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  deleteNotification,
  getNotificationList,
} from '@/service/notificationServices';
import useCommonStore from '@/store/useCommonStore';

import ViewAddEditDrawer, {
  TDrawerMode,
  INotificationData,
} from './modal/ViewAddEditDrawer';

// --- Types ---
interface INotification {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  targeted_user: string;
  status: 'Published' | 'Scheduled';
  is_sent?: boolean;
}

// --- Status config ---
const NotificationStatusConfig: Record<
  string,
  { label: string; className: string }
> = {
  Published: {
    label: 'notification.status.published',
    className: 'text-success-primary bg-success-light-50',
  },
  Scheduled: {
    label: 'notification.status.scheduled',
    className: 'text-warning-secondary bg-warning-light-50',
  },
};

const Notifications = () => {
  const { formatText } = useFormatText();
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search?.trim(), 500);
  const [data, setData] = useState<INotification[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<TDrawerMode>('add');
  const [selectedNotification, setSelectedNotification] =
    useState<INotificationData | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingNotificationId, setDeletingNotificationId] = useState<
    string | null
  >(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { loadingTable, setLoadingTable } = useCommonStore();

  const mapToDrawerData = (row: INotification): INotificationData => ({
    id: row.id,
    title: row.title,
    description: row.description,
    targeted_user: row.targeted_user.toLowerCase(),
    type: 'push',
    is_scheduled: row.status === 'Scheduled',
    date: row.date,
    time: row.time,
  });

  const openDrawer = (mode: TDrawerMode, row?: INotification) => {
    setDrawerMode(mode);
    setSelectedNotification(row ? mapToDrawerData(row) : null);
    setIsDrawerOpen(true);
  };

  const pageTitle = formatText('notification.title');

  // Set page title
  useEffect(() => {
    document.title = pageTitle;
    setTitle(pageTitle);
    setIsBreadcrumb([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle]);

  const fetchData = async () => {
    setLoadingTable(true);
    try {
      const params = {
        search: debouncedSearch || undefined,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        order: sortField ? { [sortField]: sortDirection } : undefined,
      };
      const response = await getNotificationList(params);
      if (response?.status) {
        const apiData = response?.data || [];
        const mappedData = apiData?.map((item: INotification) => ({
          ...item,
          status: item.is_sent ? 'Published' : 'Scheduled',
        }));
        setData(mappedData);
        setTotal(response?.total || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, currentPage, pageSize, sortField, sortDirection]);

  const handleDeleteClick = (id: string) => {
    setDeletingNotificationId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingNotificationId) return;
    setIsDeleting(true);
    try {
      const response = await deleteNotification(deletingNotificationId);
      if (response?.status) {
        fetchData();
        setDeletingNotificationId(null);
        setIsDeleteModalOpen(false);
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // const isEmpty = !loadingTable && data.length === 0 && !debouncedSearch;
  const isEmptySearch = !loadingTable && data.length === 0 && !!debouncedSearch;

  const handleSort = (key: string) => {
    if (sortField === key) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else if (sortDirection === 'DESC') {
        setSortField('');
        setSortDirection('');
      } else {
        setSortDirection('ASC');
      }
    } else {
      setSortField(key);
      setSortDirection('ASC');
    }
  };

  const columns = [
    {
      title: 'notification.columns.notification-title',
      key: 'title',
      sorter: true,
      handleSort: () => handleSort('title'),
      render: (row: INotification) => (
        <span className="text-sm font-medium text-neutral-primary">
          {row?.title}
        </span>
      ),
    },
    {
      title: 'notification.columns.description',
      key: 'description',
      sorter: true,
      handleSort: () => handleSort('description'),
      render: (row: INotification) => (
        <span
          className="text-sm truncate max-w-62.5 block"
          title={row?.description}
        >
          {row?.description}
        </span>
      ),
    },
    {
      title: 'notification.columns.date',
      key: 'date',
      sorter: true,
      handleSort: () => handleSort('date'),
      render: (row: INotification) => (
        <span className="text-sm">
          {row?.date ? dayjs(row.date).format('MMM DD, YYYY') : '-'}
        </span>
      ),
    },
    {
      title: 'notification.columns.time',
      key: 'time',
      sorter: true,
      handleSort: () => handleSort('time'),
      render: (row: INotification) => (
        <span className="text-sm">{row?.time ?? '-'}</span>
      ),
    },
    {
      title: 'notification.columns.targeted-users',
      key: 'targeted_user',
      sorter: true,
      handleSort: () => handleSort('targeted_user'),
      render: (row: INotification) => (
        <span className="text-sm first-letter:capitalize">
          {row?.targeted_user}
        </span>
      ),
    },
    {
      title: 'notification.columns.status',
      key: 'is_sent',
      sorter: true,
      handleSort: () => handleSort('is_sent'),
      render: (row: INotification) => {
        const config = NotificationStatusConfig[row?.status];
        return (
          <span
            className={clsx(
              'px-2 py-1 rounded-full text-xs font-semibold',
              config?.className
            )}
          >
            {formatText(config?.label || row?.status)}
          </span>
        );
      },
    },
    {
      title: 'common.actions.action',
      key: 'action',
      align: 'center',
      render: (row: INotification) => {
        const actions = [
          {
            key: 'view',
            label: 'common.actions.view',
            icon: <IconEye size={18} />,
            onClick: () => openDrawer('view', row),
          },
          {
            key: 'edit',
            label: 'common.actions.edit',
            icon: <IconEdit size={18} />,
            onClick: () => openDrawer('edit', row),
            disabled: row?.is_sent,
          },
          {
            key: 'remove',
            label: 'common.actions.remove',
            icon: <IconTrash size={18} />,
            isDanger: true,
            onClick: () => handleDeleteClick(row.id),
          },
        ];
        return <ActionDropdown actions={actions} />;
      },
    },
  ];

  const isEmptyList =
    !loadingTable &&
    data?.length === 0 &&
    search?.length === 0 &&
    debouncedSearch?.length === 0;

  return (
    <div className="flex flex-col gap-6 ps-1">
      {!isEmptyList && (
        <div className="flex items-center justify-between">
          <Input
            name="search"
            placeholder={formatText('notification.search-placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-500" />,
            }}
            className="max-w-70"
            inputClassName="py-[13px]"
          />
          <Button
            label={formatText('notification.actions.add-notification')}
            icon={<IconPlus size={18} className="me-1" />}
            onClick={() => openDrawer('add')}
          />
        </div>
      )}

      <CustomTable
        columns={columns}
        dataSource={data}
        isLoading={loadingTable}
        total={total}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        fieldName={sortField}
        sortDirection={sortDirection}
        isEmpty={isEmptyList}
        emptyImgSrc="/assets/svg/notification-empty.svg"
        emptyTitle={formatText('notification.empty-title')}
        emptyDescription={formatText('notification.empty-desc')}
        emptyButtonText={formatText('notification.actions.add-notification')}
        emptyButtonIcon={<IconPlus size={18} />}
        onEmptyBtnClick={() => openDrawer('add')}
        isEmptySearch={isEmptySearch}
        onRowClick={(id) => {
          const row = data?.find((item) => item?.id === id);
          if (row) openDrawer('view', row);
        }}
      />

      <Activity mode={isDrawerOpen ? 'visible' : 'hidden'}>
        <ViewAddEditDrawer
          isOpen={isDrawerOpen}
          setIsOpen={setIsDrawerOpen}
          mode={drawerMode}
          initialData={selectedNotification}
          onEditClick={() => setDrawerMode('edit')}
          refetch={fetchData}
        />
      </Activity>
      <Activity mode={isDeleteModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isDeleteModalOpen}
          setIsOpen={setIsDeleteModalOpen}
          title="notification.actions.delete-title"
          descriptionPrefix="notification.actions.delete-prefix"
          descriptionSuffix="notification.actions.delete-suffix"
          confirmLabel={formatText('notification.actions.delete-confirm')}
          onConfirm={handleConfirmDelete}
          isLoading={isDeleting}
          isDestructive
        />
      </Activity>
    </div>
  );
};

export default Notifications;
